import { useEffect, useRef, useState, type CSSProperties, type ReactNode, type SyntheticEvent } from 'react';
import { cx } from '../cx';
import { rem } from '../units';
import { initials } from '../Avatar/Avatar';

export type LogoTileSize = 'sm' | 'md' | 'lg';

export type LogoTileProps = {
  src?: string | null;
  /** The employer's name: the image's alt text and the source of the initials. */
  name: string;
  /** The company key, exposed as data-company for tests and analytics. */
  companyKey?: string;
  size?: LogoTileSize;
  /**
   * Width over height of the logo, when known (saves a layout shift). Otherwise it is read from the
   * image once loaded. Above 1.4 the tile widens for the wordmark; squarer marks get a square tile.
   */
  aspect?: number;
  /** A ring in the employer's colour, e.g. where the tile overlaps its brand strip. Any CSS colour. */
  ringColor?: string;
  /** Backdrop for a monochrome mark that would vanish on white (from the logo classifier). */
  backdrop?: 'mono-light' | 'mono-dark';
  /**
   * Custom logo content in place of `src` (e.g. an app logo component with its own URL fallbacks),
   * rendered inside the tile. The tile still widens for a wordmark: it reads the size of the first
   * image that loads inside it.
   */
  children?: ReactNode;
  /**
   * Keep the tile square below the `sm` breakpoint even for a wordmark. A wide tile that appears
   * once the image has loaded would crush the text beside it on a phone and shift the whole row.
   */
  squareOnPhone?: boolean;
  className?: string;
};

const HEIGHT: Record<LogoTileSize, number> = { sm: 36, md: 52, lg: 88 };
const RADIUS: Record<LogoTileSize, string> = { sm: 'rounded-jc-info', md: 'rounded-jc-info', lg: 'rounded-jc-info' };
const TEXT: Record<LogoTileSize, string> = { sm: 'text-sm', md: 'text-lg', lg: 'type-figure-lg' };
/**
 * A logo passed as children (CompanyLogo, often inside a link) fills the tile, less a small inset, instead
 * of keeping its own fixed square: in a tile widened for a wordmark the fixed square left the logo a
 * sliver in the middle (2026-10-15). `!` because CompanyLogo sizes itself with an inline style.
 */
const CHILD_FILL: Record<LogoTileSize, string> = {
  sm: '[&>*]:!size-full [&>a>*]:!size-full [&>*]:!p-1',
  md: '[&>*]:!size-full [&>a>*]:!size-full [&>*]:!p-1.5',
  lg: '[&>*]:!size-full [&>a>*]:!size-full [&>*]:!p-2.5',
};
export const WIDE_LOGO_ASPECT = 1.4;
/** The widest a wide tile gets, relative to its height (52 tall, up to 132 wide). */
const MAX_WIDE = 132 / 52;

/** Tile width for a logo of the given aspect at the given height. Pure, exported for tests. */
export function logoTileWidth(height: number, aspect: number | undefined): number {
  if (!aspect || aspect <= WIDE_LOGO_ASPECT) return height;
  return Math.round(height * Math.min(aspect, MAX_WIDE));
}

/**
 * The employer's logo on a tile that stays white in every theme, contained and never cropped or
 * recoloured, with an initials fallback. Wordmarks get a wide tile so they are not shrunk to a sliver.
 */
export function LogoTile({
  src,
  name,
  companyKey,
  size = 'md',
  aspect,
  ringColor,
  backdrop,
  children,
  squareOnPhone,
  className,
}: LogoTileProps) {
  const [failed, setFailed] = useState(false);
  const [measured, setMeasured] = useState<number | undefined>(undefined);
  const ref = useRef<HTMLSpanElement>(null);
  // On a server-rendered page an image that 404s fails before React hydrates, so onError never
  // fires and the browser's broken-image icon stays. Once mounted, re-check and fall back.
  useEffect(() => {
    const img = ref.current?.querySelector('img');
    if (img && img.complete && img.naturalWidth === 0) setFailed(true);
  }, [src]);
  // Same race for the width: a cached logo can finish loading before hydration, so onLoad never fires
  // and a wordmark keeps a square tile. Measure an already-loaded image after each render until known.
  useEffect(() => {
    if (aspect || measured) return;
    const img = ref.current?.querySelector('img');
    if (img && img.complete && img.naturalHeight > 0) setMeasured(img.naturalWidth / img.naturalHeight);
  });
  const height = HEIGHT[size];
  const showImage = !children && !!src && !failed;
  const width = showImage || children ? logoTileWidth(height, aspect ?? measured) : height;
  const measure = (e: SyntheticEvent<HTMLElement>) => {
    const img = e.target as HTMLImageElement;
    if (!aspect && img.tagName === 'IMG' && img.naturalHeight > 0) setMeasured(img.naturalWidth / img.naturalHeight);
  };
  const style: CSSProperties & Record<'--jc-tile-ring' | '--jc-tile-h', string | undefined> = {
    width: rem(width),
    height: rem(height),
    '--jc-tile-ring': ringColor,
    '--jc-tile-h': squareOnPhone ? rem(height) : undefined,
  };
  return (
    <span
      ref={ref}
      style={style}
      data-company={companyKey}
      onLoadCapture={children ? measure : undefined}
      className={cx(
        'inline-flex shrink-0 items-center justify-center overflow-hidden ring-1 ring-inset ring-jc-card-border',
        ringColor && 'ring-2 ring-(--jc-tile-ring)',
        backdrop === 'mono-light'
          ? 'bg-jc-logo-mono-light'
          : backdrop === 'mono-dark'
            ? 'bg-jc-logo-mono-dark'
            : 'bg-jc-logo-surface',
        squareOnPhone && 'max-sm:max-w-(--jc-tile-h)',
        children && CHILD_FILL[size],
        RADIUS[size],
        className,
      )}
    >
      {children ? (
        children
      ) : showImage ? (
        <img
          src={src}
          alt={name}
          className="max-h-full max-w-full object-contain p-1.5"
          onError={() => setFailed(true)}
          onLoad={measure}
        />
      ) : (
        <span role="img" aria-label={name} className={cx('font-display font-bold text-jc-on-logo-surface', TEXT[size])}>
          <span aria-hidden="true">{initials(name)}</span>
        </span>
      )}
    </span>
  );
}
